import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Edit2, Trash2, Filter, X, Search } from 'lucide-react';
import '../App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CompetitorPricing = () => {
  const { getAuthHeader } = useAuth();
  const [entries, setEntries] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterService, setFilterService] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [countries, setCountries] = useState([]);
  const [schengenCountries, setSchengenCountries] = useState([]);
  const [showSchengenSelector, setShowSchengenSelector] = useState(false);
  const [showOtherCountryInput, setShowOtherCountryInput] = useState(false);
  
  const [formData, setFormData] = useState({
    competitor_name: '',
    service_type: '',
    destination_country: '',
    schengen_country: '',
    other_country: '',
    base_price: '',
    embassy_fees: '',
    vfs_bls_charges: '',
    other_charges: '',
    success_rate: '',
    processing_time: '',
    notes: ''
  });

  useEffect(() => {
    fetchServices();
    fetchCountries();
    fetchSchengenCountries();
    fetchEntries();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`, { headers: getAuthHeader() });
      setServices(response.data);
    } catch (error) {
      console.error('Failed to fetch services:', error);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/countries`);
      setCountries(response.data);
    } catch (error) {
      console.error('Failed to fetch countries:', error);
    }
  };

  const fetchSchengenCountries = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/schengen-countries`);
      setSchengenCountries(response.data);
    } catch (error) {
      console.error('Failed to fetch Schengen countries:', error);
    }
  };

  const fetchEntries = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterService) params.service_type = filterService;
      if (filterCountry) params.country = filterCountry;
      
      const response = await axios.get(`${API}/admin/competitor-pricing`, {
        params,
        headers: getAuthHeader()
      });
      setEntries(response.data);
    } catch (error) {
      console.error('Failed to fetch entries:', error);
      alert('Failed to fetch competitor pricing data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, [filterService, filterCountry]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Determine the final country value
    let finalCountry = formData.destination_country;
    if (formData.destination_country === 'Schengen (Europe)') {
      finalCountry = formData.schengen_country;
    } else if (formData.destination_country === 'Other') {
      finalCountry = formData.other_country;
    }

    if (!finalCountry) {
      alert('Please select or specify a destination country');
      return;
    }

    const submitData = {
      ...formData,
      country: finalCountry,
      base_price: parseFloat(formData.base_price) || 0,
      embassy_fees: parseFloat(formData.embassy_fees) || 0,
      vfs_bls_charges: parseFloat(formData.vfs_bls_charges) || 0,
      other_charges: parseFloat(formData.other_charges) || 0
    };
    
    try {
      if (editingEntry) {
        await axios.put(
          `${API}/admin/competitor-pricing/${editingEntry.id}`,
          submitData,
          { headers: getAuthHeader() }
        );
      } else {
        await axios.post(
          `${API}/admin/competitor-pricing`,
          submitData,
          { headers: getAuthHeader() }
        );
      }
      
      setShowModal(false);
      resetForm();
      fetchEntries();
    } catch (error) {
      console.error('Failed to save entry:', error);
      alert(error.response?.data?.detail || 'Failed to save entry');
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    
    // Check if the country is in the main countries list
    const isMainCountry = countries.some(c => c.name === entry.country);
    const isSchengenCountry = schengenCountries.some(c => c.name === entry.country);
    
    let destination_country = entry.country;
    let schengen_country = '';
    let other_country = '';
    
    if (isSchengenCountry && !isMainCountry) {
      destination_country = 'Schengen (Europe)';
      schengen_country = entry.country;
      setShowSchengenSelector(true);
      setShowOtherCountryInput(false);
    } else if (!isMainCountry && !isSchengenCountry) {
      destination_country = 'Other';
      other_country = entry.country;
      setShowSchengenSelector(false);
      setShowOtherCountryInput(true);
    } else {
      setShowSchengenSelector(false);
      setShowOtherCountryInput(false);
    }
    
    setFormData({
      competitor_name: entry.competitor_name,
      service_type: entry.service_type,
      destination_country,
      schengen_country,
      other_country,
      base_price: entry.base_price,
      embassy_fees: entry.embassy_fees,
      vfs_bls_charges: entry.vfs_bls_charges,
      other_charges: entry.other_charges,
      success_rate: entry.success_rate || '',
      processing_time: entry.processing_time || '',
      notes: entry.notes || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    
    try {
      await axios.delete(`${API}/admin/competitor-pricing/${id}`, {
        headers: getAuthHeader()
      });
      fetchEntries();
    } catch (error) {
      console.error('Failed to delete entry:', error);
      alert('Failed to delete entry');
    }
  };

  const resetForm = () => {
    setFormData({
      competitor_name: '',
      service_type: '',
      destination_country: '',
      schengen_country: '',
      other_country: '',
      base_price: '',
      embassy_fees: '',
      vfs_bls_charges: '',
      other_charges: '',
      success_rate: '',
      processing_time: '',
      notes: ''
    });
    setEditingEntry(null);
    setShowSchengenSelector(false);
    setShowOtherCountryInput(false);
  };

  const formatCurrency = (amount) => {
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const filteredEntries = entries.filter(entry =>
    entry.competitor_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Competitor Pricing Intelligence</h1>
          <p className="page-description">Track and analyze competitor pricing data (Super Admin Only)</p>
        </div>
        <button className="btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
          <Plus size={20} />
          Add Competitor Entry
        </button>
      </div>

      {/* Filters */}
      <div className="filters-bar" style={{ marginBottom: '24px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        <div className="search-box" style={{ flex: '1', minWidth: '250px' }}>
          <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#666' }} />
          <input
            type="text"
            placeholder="Search competitor name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ paddingLeft: '40px', width: '100%' }}
          />
        </div>
        
        <select value={filterService} onChange={(e) => setFilterService(e.target.value)} style={{ minWidth: '200px' }}>
          <option value="">All Services</option>
          {services.map(svc => (
            <option key={svc.id} value={svc.title}>{svc.title}</option>
          ))}
        </select>

        <input
          type="text"
          placeholder="Filter by country..."
          value={filterCountry}
          onChange={(e) => setFilterCountry(e.target.value)}
          style={{ minWidth: '200px' }}
        />

        {(filterService || filterCountry || searchTerm) && (
          <button className="btn-secondary" onClick={() => { setFilterService(''); setFilterCountry(''); setSearchTerm(''); }}>
            <X size={16} /> Clear Filters
          </button>
        )}
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <p>Loading...</p>
          </div>
        ) : filteredEntries.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <p>No competitor pricing data found. Add your first entry to get started.</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Competitor</th>
                  <th>Service</th>
                  <th>Country</th>
                  <th>Base Price</th>
                  <th>Embassy Fees</th>
                  <th>VFS/BLS</th>
                  <th>Other</th>
                  <th>Total</th>
                  <th>Success Rate</th>
                  <th>ETA</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredEntries.map(entry => (
                  <tr key={entry.id}>
                    <td><strong>{entry.competitor_name}</strong></td>
                    <td>{entry.service_type}</td>
                    <td>{entry.country}</td>
                    <td>{formatCurrency(entry.base_price)}</td>
                    <td>{formatCurrency(entry.embassy_fees)}</td>
                    <td>{formatCurrency(entry.vfs_bls_charges)}</td>
                    <td>{formatCurrency(entry.other_charges)}</td>
                    <td><strong>{formatCurrency(entry.total_price)}</strong></td>
                    <td>{entry.success_rate || '-'}</td>
                    <td>{entry.processing_time || '-'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button className="btn-icon" onClick={() => handleEdit(entry)} title="Edit">
                          <Edit2 size={16} />
                        </button>
                        <button className="btn-icon btn-danger" onClick={() => handleDelete(entry.id)} title="Delete">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
            <div className="modal-header">
              <h2>{editingEntry ? 'Edit' : 'Add'} Competitor Entry</h2>
              <button className="btn-icon" onClick={() => setShowModal(false)}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>Competitor Name *</label>
                  <input
                    type="text"
                    value={formData.competitor_name}
                    onChange={(e) => setFormData({...formData, competitor_name: e.target.value})}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Service Type *</label>
                  <select
                    value={formData.service_type}
                    onChange={(e) => setFormData({...formData, service_type: e.target.value})}
                    required
                  >
                    <option value="">Select Service</option>
                    {services.map(svc => (
                      <option key={svc.id} value={svc.title}>{svc.title}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Destination Country *</label>
                  <select
                    value={formData.destination_country}
                    onChange={(e) => {
                      const selectedCountry = e.target.value;
                      setFormData({ 
                        ...formData, 
                        destination_country: selectedCountry,
                        schengen_country: selectedCountry === 'Schengen (Europe)' ? formData.schengen_country : '',
                        other_country: selectedCountry === 'Other' ? formData.other_country : ''
                      });
                      setShowSchengenSelector(selectedCountry === 'Schengen (Europe)');
                      setShowOtherCountryInput(selectedCountry === 'Other');
                    }}
                    required
                  >
                    <option value="">Select Country</option>
                    {countries.map(country => (
                      <option key={country.name} value={country.name}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Schengen Country Selector */}
                {showSchengenSelector && (
                  <div className="form-group" style={{ background: '#f0f9ff', padding: '12px', borderRadius: '6px', border: '1px solid #bae6fd', gridColumn: '1 / -1' }}>
                    <label>Select Specific Schengen Country *</label>
                    <select
                      value={formData.schengen_country || ''}
                      onChange={(e) => setFormData({ ...formData, schengen_country: e.target.value })}
                      required
                      style={{ background: 'white' }}
                    >
                      <option value="">Select Schengen Country</option>
                      {schengenCountries.map(country => (
                        <option key={country.code} value={country.name}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                    <small style={{ display: 'block', marginTop: '6px', color: '#0369a1', fontSize: '12px' }}>
                      💡 Select the primary destination country within Schengen area
                    </small>
                  </div>
                )}

                {/* Other Country Input */}
                {showOtherCountryInput && (
                  <div className="form-group" style={{ background: '#fef3c7', padding: '12px', borderRadius: '6px', border: '1px solid #fcd34d', gridColumn: '1 / -1' }}>
                    <label>Specify Country Name *</label>
                    <input
                      type="text"
                      value={formData.other_country || ''}
                      onChange={(e) => setFormData({ ...formData, other_country: e.target.value })}
                      placeholder="Enter country name..."
                      required
                      style={{ background: 'white' }}
                    />
                    <small style={{ display: 'block', marginTop: '6px', color: '#92400e', fontSize: '12px' }}>
                      💡 Please enter the destination country not listed above
                    </small>
                  </div>
                )}

                <div className="form-group">
                  <label>Base Price (₹)</label>
                  <input
                    type="number"
                    value={formData.base_price}
                    onChange={(e) => setFormData({...formData, base_price: e.target.value})}
                    onFocus={(e) => e.target.select()}
                    placeholder="0"
                    min="0"
                  />
                </div>

                <div className="form-group">
                  <label>Embassy Fees (₹)</label>
                  <input
                    type="number"
                    value={formData.embassy_fees}
                    onChange={(e) => setFormData({...formData, embassy_fees: e.target.value})}
                    onFocus={(e) => e.target.select()}
                    placeholder="0"
                    min="0"
                  />
                </div>

                <div className="form-group">
                  <label>VFS/BLS Charges (₹)</label>
                  <input
                    type="number"
                    value={formData.vfs_bls_charges}
                    onChange={(e) => setFormData({...formData, vfs_bls_charges: e.target.value})}
                    onFocus={(e) => e.target.select()}
                    placeholder="0"
                    min="0"
                  />
                </div>

                <div className="form-group">
                  <label>Other Charges (₹)</label>
                  <input
                    type="number"
                    value={formData.other_charges}
                    onChange={(e) => setFormData({...formData, other_charges: e.target.value})}
                    onFocus={(e) => e.target.select()}
                    placeholder="0"
                    min="0"
                  />
                </div>

                <div className="form-group">
                  <label>Success Rate (e.g., 95%)</label>
                  <input
                    type="text"
                    value={formData.success_rate}
                    onChange={(e) => setFormData({...formData, success_rate: e.target.value})}
                  />
                </div>

                <div className="form-group">
                  <label>Processing Time (e.g., 30-45 days)</label>
                  <input
                    type="text"
                    value={formData.processing_time}
                    onChange={(e) => setFormData({...formData, processing_time: e.target.value})}
                  />
                </div>

                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    rows="3"
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingEntry ? 'Update' : 'Create'} Entry
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompetitorPricing;
